import type { ReactionId } from '@zip2live/shared';
import { viewerId } from './viewer-id.ts';

/**
 * Floating reactions: taps go out in one request about once a second; batches from everyone come
 * back over the stream socket. Your own taps float at once, and are taken out of the next batch
 * that comes back so they don't float twice.
 */
type Counts = Partial<Record<ReactionId, number>>;
type Listener = (counts: Counts) => void;

const listeners = new Map<string, Set<Listener>>();
const unsent = new Map<string, Counts>();
const echoes = new Map<string, Counts>();
let timer: ReturnType<typeof setTimeout> | null = null;

export function onReactions(streamId: string, fn: Listener) {
  let set = listeners.get(streamId);
  if (!set) listeners.set(streamId, (set = new Set()));
  set.add(fn);
  return () => void set.delete(fn);
}

const emit = (streamId: string, counts: Counts) => listeners.get(streamId)?.forEach((fn) => fn(counts));

/** A batch from the stream socket. */
export function reactionsArrived(streamId: string, counts: Counts) {
  const mine = echoes.get(streamId) ?? {};
  const others: Counts = {};
  for (const [id, n] of Object.entries(counts) as [ReactionId, number][]) {
    const own = Math.min(mine[id] ?? 0, n);
    if (own) mine[id] = (mine[id] ?? 0) - own;
    if (n - own > 0) others[id] = n - own;
  }
  if (Object.keys(others).length) emit(streamId, others);
}

export function react(streamId: string, id: ReactionId) {
  emit(streamId, { [id]: 1 });
  const counts = unsent.get(streamId) ?? {};
  counts[id] = (counts[id] ?? 0) + 1;
  unsent.set(streamId, counts);
  timer ??= setTimeout(flush, 1000);
}

function flush() {
  timer = null;
  for (const [streamId, counts] of unsent) {
    unsent.delete(streamId);
    const capped = Object.fromEntries(Object.entries(counts).map(([k, n]) => [k, Math.min(n, 20)])) as Counts;
    void fetch(`/api/v1/streams/${encodeURIComponent(streamId)}/reactions?v=${viewerId()}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ counts: capped }),
    })
      .then((r) => r.json() as Promise<{ accepted?: number }>)
      .then(({ accepted }) => {
        if (!accepted) return;
        // Expect our own taps back in the room's next batch.
        const echo = echoes.get(streamId) ?? {};
        let left = accepted;
        for (const [id, n] of Object.entries(capped) as [ReactionId, number][]) {
          const take = Math.min(n, left);
          echo[id] = (echo[id] ?? 0) + take;
          left -= take;
        }
        echoes.set(streamId, echo);
        setTimeout(() => echoes.delete(streamId), 3000);
      })
      .catch(() => undefined);
  }
}

/** Your vote on a stream, remembered on this device (the server keeps the real one). */
export function savedVote(streamId: string): 1 | -1 | 0 {
  try {
    const v = Number(localStorage.getItem(`z2l:vote:${streamId}`));
    return v === 1 || v === -1 ? v : 0;
  } catch {
    return 0;
  }
}

export function saveVote(streamId: string, value: 1 | -1 | 0) {
  try {
    if (value) localStorage.setItem(`z2l:vote:${streamId}`, String(value));
    else localStorage.removeItem(`z2l:vote:${streamId}`);
  } catch {
    /* storage blocked */
  }
}
